import React from 'react';

import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { LoaderError } from './LoaderError';
import type { LoadingComponent } from './publicTypes';

type LoadableComponentType<Props> = React.ComponentType<Props> & LoadableExport.LoadableComponent;

const UFOLoading = ({
	Loading,
	...props
}: { Loading: LoadingComponent | undefined } & React.ComponentProps<LoadingComponent>) => (
	<UFOLoadHold name="loadable-hold">{Loading ? <Loading {...props} /> : null}</UFOLoadHold>
);

function noop() {}

type LoadableErrorBoundaryProps = {
	LoadingComp: LoadingComponent;
	onRetry: () => void;
	children: React.ReactNode;
};

type LoadableErrorBoundaryState = {
	error: unknown;
};

// Bridges React.lazy's error semantics back to the LoadingComponent
// `error` prop that consumers of @townsquare/loadable rely on. Without
// this, a loader rejection would propagate as an uncaught exception
// instead of rendering an in-place error state.
//
// Only loader rejections are handled here; render-time errors from the loaded
// component are rethrown to an app-level boundary.
class LoadableErrorBoundary extends React.Component<
	LoadableErrorBoundaryProps,
	LoadableErrorBoundaryState
> {
	state: LoadableErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: unknown): LoadableErrorBoundaryState {
		return { error };
	}

	private retry = () => {
		// React.lazy caches the rejection in its internal payload and won't
		// re-invoke the loader on a plain re-render. The owning component
		// owns the lazy instance; bump its version via onRetry so a fresh
		// React.lazy is constructed (and the loader runs again).
		this.props.onRetry();
		this.setState({ error: null });
	};

	render() {
		const { error } = this.state;
		const { LoadingComp, children } = this.props;
		if (error !== null) {
			if (!(error instanceof LoaderError)) {
				throw error;
			}
			return (
				<LoadingComp
					isLoading={false}
					pastDelay={false}
					timedOut={false}
					error={error.cause}
					retry={this.retry}
				/>
			);
		}
		return children;
	}
}

// Self-contained component encapsulating the retry/Suspense/error-boundary
// wiring for the React.lazy path. Keeping it here makes buildLazyLoadable
// a thin setup function rather than a mix of setup + render logic.
function RetryableLazy<Props>({
	loadModule,
	LoadingComp,
	cachedPromiseRef,
	...props
}: {
	loadModule: () => Promise<{ default: React.ComponentType<Props> }>;
	LoadingComp: LoadingComponent;
	cachedPromiseRef: { current: Promise<{ default: React.ComponentType<Props> }> | null };
} & Props) {
	const [version, setVersion] = React.useState(0);
	const Lazy = React.useMemo(
		() => React.lazy(loadModule) as unknown as React.ComponentType<object>,
		// version intentionally drives the re-creation
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[version],
	);
	const onRetry = React.useCallback(() => {
		// Clear the wrapper's promise cache too so we don't hand back the
		// rejected one to the freshly-created React.lazy.
		cachedPromiseRef.current = null;
		setVersion((v) => v + 1);
	}, [cachedPromiseRef]);

	return (
		<LoadableErrorBoundary LoadingComp={LoadingComp} onRetry={onRetry}>
			<React.Suspense
				fallback={
					<UFOLoading
						Loading={LoadingComp}
						isLoading
						pastDelay={false}
						timedOut={false}
						error={null}
						retry={noop}
					/>
				}
			>
				<Lazy {...(props as object)} />
			</React.Suspense>
		</LoadableErrorBoundary>
	);
}

function buildLazyLoadable<Props, Exports extends object>(
	options: LoadableExport.Options<Props, Exports>,
): Branch<Props> {
	const { loading: LoadingComp } = options;

	// Normalise the loader to React.lazy's `{ default }` shape. `render` and
	// `loader` are read together so their types correlate across the options
	// union (react-loadable's `render` picks a non-default export).
	const resolveModule: () => Promise<{ default: React.ComponentType<Props> }> =
		'render' in options
			? () =>
					options.loader().then((loaded) => {
						const Rendered: React.ComponentType<Props> = (props) => (
							<>{options.render(loaded, props)}</>
						);
						return { default: Rendered };
					})
			: () =>
					options.loader().then((loaded) => ({
						default: 'default' in loaded ? loaded.default : loaded,
					}));

	// Memoise the loader promise so .preload() and the first render share
	// it. Clear on rejection so the error-boundary retry path can re-load.
	const cachedPromiseRef: { current: Promise<{ default: React.ComponentType<Props> }> | null } = {
		current: null,
	};
	const loadModule = () => {
		if (!cachedPromiseRef.current) {
			cachedPromiseRef.current = resolveModule().catch((error) => {
				cachedPromiseRef.current = null;
				throw new LoaderError(error);
			});
		}
		return cachedPromiseRef.current;
	};

	const LazyComponent: React.ComponentType<Props> = (props) => (
		<RetryableLazy
			loadModule={loadModule}
			LoadingComp={LoadingComp}
			cachedPromiseRef={cachedPromiseRef}
			{...props}
		/>
	);

	return { Component: LazyComponent, preload: loadModule };
}

type Branch<Props> = { Component: React.ComponentType<Props>; preload: () => Promise<unknown> };

const Loadable = <Props, Exports extends object>(
	options: LoadableExport.Options<Props, Exports>,
): LoadableComponentType<Props> => {
	const { Component, preload } = buildLazyLoadable<Props, Exports>(options);

	const LoadableComponent = (props: Props) => {
		const C = Component as React.ComponentType<object>;
		return <C {...(props as object)} />;
	};

	// Give the wrapper a discoverable name in DevTools / error stacks.
	// Derive from the loader function name where possible (rsbuild / webpack
	// produce names like `LoadableComponent_loader__WEBPACK_IMPORTED_MODULE_…`
	// — fall back to plain "Loadable" if nothing useful is exposed).
	const loaderName = (options.loader as { name?: string }).name;
	LoadableComponent.displayName = loaderName ? `Loadable(${loaderName})` : 'Loadable';

	(LoadableComponent as unknown as LoadableComponentType<Props>).preload = preload;

	return LoadableComponent as unknown as LoadableComponentType<Props>;
};

export default Loadable;
